import { useCallback, useEffect, useState } from 'react';
import { EmailSettings } from '../../components/EmailSettings';
import { api, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { relativeTime, titleCase } from '../../lib/format';

interface TemplateRow {
  event: string;
  channel: string;
  essential: boolean;
  configured: boolean;
  version: number | null;
  subject: string | null;
  updatedAt: string | null;
}

interface VariableDoc {
  name: string;
  description: string;
  sample: string | number;
}

interface TemplateDetail {
  event: string;
  channel: string;
  variables: VariableDoc[];
  template: { subject: string; body: string; version: number } | null;
}

interface Preview {
  subject: string;
  body: string;
  text: string;
  missing: string[];
}

interface QueueRow {
  id: string;
  event: string;
  channel: string;
  recipient: string;
  status: string;
  attempts: number;
  providerRef: string | null;
  failureReason: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
}

/**
 * The template manager and the delivery queue (BRD FR-NTF-003, FR-NTF-007).
 *
 * The two live on one screen because they answer the same question in
 * sequence: what would we send, and what actually happened when we sent it.
 */
export function NotificationTemplates() {
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [selected, setSelected] = useState<{ event: string; channel: string } | null>(null);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [queue, setQueue] = useState<QueueRow[] | null>(null);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(() => {
    api<{ items: TemplateRow[] }>('/admin/notifications/templates')
      .then((result) => setRows(result.items))
      .catch((caught) => setError(caught as ApiError));
  }, []);

  const loadQueue = useCallback(() => {
    api<{ items: QueueRow[]; summary: Record<string, number> }>(
      `/admin/notifications/queue?pageSize=15${statusFilter ? `&status=${statusFilter}` : ''}`,
    )
      .then((result) => {
        setQueue(result.items);
        setSummary(result.summary);
      })
      .catch((caught) => setError(caught as ApiError));
  }, [statusFilter]);

  useEffect(loadList, [loadList]);
  useEffect(loadQueue, [loadQueue]);

  const openTemplate = useCallback((event: string, channel: string) => {
    setSelected({ event, channel });
    setDetail(null);
    setPreview(null);
    setError(null);
    setNotice(null);
    api<TemplateDetail>(`/admin/notifications/templates/${event}/${channel}`)
      .then((result) => {
        setDetail(result);
        setSubject(result.template?.subject ?? '');
        setBody(result.template?.body ?? '');
      })
      .catch((caught) => setError(caught as ApiError));
  }, []);

  async function runPreview() {
    if (!selected) return;
    setError(null);
    try {
      const result = await api<Preview>(
        `/admin/notifications/templates/${selected.event}/${selected.channel}/preview`,
        { method: 'POST', body: JSON.stringify({ subject, body }) },
      );
      setPreview(result);
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function save() {
    if (!selected) return;
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const result = await api<{ version: number }>(
        `/admin/notifications/templates/${selected.event}/${selected.channel}`,
        { method: 'PUT', body: JSON.stringify({ subject, body }) },
      );
      setNotice(`Saved as version ${result.version}. Messages already sent keep their old version.`);
      loadList();
      openTemplate(selected.event, selected.channel);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setSaving(false);
    }
  }

  async function testSend() {
    if (!selected) return;
    setError(null);
    setNotice(null);
    try {
      const result = await api<{ sentTo: string }>(
        `/admin/notifications/templates/${selected.event}/${selected.channel}/test`,
        { method: 'POST' },
      );
      setNotice(`Queued a test to ${result.sentTo}. Drain the queue below to deliver it.`);
      loadQueue();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function drain() {
    setNotice(null);
    try {
      const result = await api<{ sent: number; failed: number; deadLettered: number }>(
        '/admin/notifications/queue/drain',
        { method: 'POST' },
      );
      setNotice(
        `Delivery pass finished: ${result.sent} sent, ${result.failed} failed, ${result.deadLettered} dead-lettered.`,
      );
      loadQueue();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function retry(id: string) {
    try {
      await api(`/admin/notifications/queue/${id}/retry`, { method: 'POST' });
      setNotice('Requeued.');
      loadQueue();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  const missingCount = rows?.filter((row) => !row.configured).length ?? 0;

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Notification templates</h1>
        <p className="page__lede">
          What every message says, and what happened when it was sent. Editing a template never
          rewrites what recipients have already received — each send records the version it used.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}

      <EmailSettings />
      {notice ? <Alert kind="success" title={notice} /> : null}
      {missingCount > 0 ? (
        <Alert
          kind="warning"
          title={`${missingCount} event and channel combinations have no template. Those events send nothing.`}
        />
      ) : null}

      <div className="tmpl">
        <div className="card tmpl__list">
          <div className="card__head">
            <span className="card__title">Events</span>
          </div>
          {!rows ? (
            <div className="spinner">Loading…</div>
          ) : (
            <ul className="tmpl__events">
              {rows.map((row) => {
                const on = selected?.event === row.event && selected.channel === row.channel;
                return (
                  <li key={`${row.event}:${row.channel}`}>
                    <button
                      className={`tmpl__event${on ? ' tmpl__event--on' : ''}`}
                      onClick={() => openTemplate(row.event, row.channel)}
                    >
                      <span className="tmpl__event-name">{titleCase(row.event)}</span>
                      <span className="tmpl__event-meta">
                        <span className="pill pill--quiet">{titleCase(row.channel)}</span>
                        {row.configured ? (
                          <span className="muted">v{row.version}</span>
                        ) : (
                          <span className="pill pill--warn">none</span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="tmpl__editor">
          {!selected ? (
            <div className="card">
              <div className="empty">Pick an event to edit its message.</div>
            </div>
          ) : !detail ? (
            <div className="card">
              <div className="spinner">Loading…</div>
            </div>
          ) : (
            <>
              <div className="card">
                <div className="card__head">
                  <span className="card__title">
                    {titleCase(detail.event)} · {titleCase(detail.channel)}
                    {detail.template ? (
                      <span className="muted" style={{ fontWeight: 400 }}>
                        {' '}
                        — version {detail.template.version}
                      </span>
                    ) : (
                      <span className="pill pill--warn" style={{ marginLeft: 8 }}>
                        not configured
                      </span>
                    )}
                  </span>
                </div>
                <div className="card__body stack">
                  <label className="field">
                    <span className="field__label">Subject</span>
                    <input
                      className="field__input"
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">
                      Body {detail.channel === 'EMAIL' ? '(HTML)' : '(plain text)'}
                    </span>
                    <textarea
                      className="field__input tmpl__body"
                      rows={14}
                      value={body}
                      onChange={(event) => setBody(event.target.value)}
                    />
                  </label>

                  <div className="row" style={{ gap: 8 }}>
                    <button className="btn" onClick={() => void save()} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn--secondary" onClick={() => void runPreview()}>
                      Preview
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => void testSend()}
                      disabled={!detail.template}
                      title={
                        detail.template
                          ? 'Sends to your own address only'
                          : 'Save the template first'
                      }
                    >
                      Send me a test
                    </button>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card__head">
                  <span className="card__title">Variables you may use</span>
                </div>
                <table className="table">
                  <tbody>
                    {detail.variables.map((variable) => (
                      <tr key={variable.name}>
                        <td style={{ width: 170 }}>
                          <button
                            className="tmpl__var"
                            title="Insert at the end of the body"
                            onClick={() => setBody((current) => `${current}{{${variable.name}}}`)}
                          >
                            {`{{${variable.name}}}`}
                          </button>
                        </td>
                        <td>
                          {variable.description}{' '}
                          <span className="muted">e.g. {String(variable.sample)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {preview ? (
                <div className="card">
                  <div className="card__head">
                    <span className="card__title">Preview, with sample values</span>
                  </div>
                  <div className="card__body">
                    {preview.missing.length > 0 ? (
                      <Alert
                        kind="warning"
                        title={`These would reach recipients unrendered: ${preview.missing
                          .map((name) => `{{${name}}}`)
                          .join(', ')}`}
                      />
                    ) : null}
                    <p style={{ marginTop: 0 }}>
                      <strong>Subject:</strong> {preview.subject}
                    </p>
                    {detail.channel === 'EMAIL' ? (
                      <iframe
                        className="tmpl__preview"
                        title="Rendered email"
                        // Rendered in a sandboxed frame: the body is
                        // administrator-authored HTML and must not be able to
                        // script the admin page it is previewed on.
                        sandbox=""
                        srcDoc={preview.body}
                      />
                    ) : (
                      <div className="tmpl__plain">{preview.body}</div>
                    )}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card__head">
          <span className="card__title">Delivery queue</span>
          <div className="row" style={{ gap: 8 }}>
            <select
              className="field__input field__input--sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              {['QUEUED', 'SENT', 'FAILED', 'DEAD_LETTER', 'SUPPRESSED'].map((status) => (
                <option key={status} value={status}>
                  {titleCase(status)}
                  {summary[status] !== undefined ? ` (${summary[status]})` : ''}
                </option>
              ))}
            </select>
            <button className="btn btn--sm" onClick={() => void drain()}>
              Run a delivery pass
            </button>
          </div>
        </div>

        {!queue ? (
          <div className="spinner">Loading…</div>
        ) : queue.length === 0 ? (
          <div className="empty">Nothing matching.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Recipient</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Detail</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.id}>
                  <td>{titleCase(row.event)}</td>
                  <td className="muted">{row.recipient}</td>
                  <td>{titleCase(row.channel)}</td>
                  <td>
                    <span className={`pill pill--${pillFor(row.status)}`}>
                      {titleCase(row.status)}
                    </span>
                  </td>
                  <td>{row.attempts}</td>
                  <td className="muted" style={{ maxWidth: 280 }}>
                    {row.failureReason ?? row.providerRef ?? '—'}
                  </td>
                  <td className="muted">{relativeTime(row.createdAt)}</td>
                  <td>
                    {row.status === 'FAILED' || row.status === 'DEAD_LETTER' ? (
                      <button className="btn btn--quiet btn--sm" onClick={() => void retry(row.id)}>
                        Retry
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function pillFor(status: string): string {
  if (status === 'SENT') return 'ok';
  if (status === 'DEAD_LETTER' || status === 'FAILED') return 'warn';
  return 'quiet';
}
